import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import { Link as RouterLink } from 'react-router-dom';
import { InvestmentDTO } from '@inversor-hub/types';
import { useGetInvestmentFinancialMetrics } from '@/lib/api/investment-financials';
import { formatDate, formatPercentage } from '@/utils/misc';
import { AssetReturnCards } from './AssetReturnCards';
import { CashflowWaterfall } from './CashflowWaterfall';
import { ClassifyLinesDialog } from './ClassifyLinesDialog';
import { DataQualityPanel } from './DataQualityPanel';
import { EquityReturnCards } from './EquityReturnCards';
import { FinancingPanel } from './FinancingPanel';
import { OperatingExpensesBreakdown } from './OperatingExpensesBreakdown';
import { PositionCard } from './PositionCard';
import { SectionHeading } from '../shared/SectionHeading';
import { TotalReturnCard } from './TotalReturnCard';
import { ValuationsDialog } from './ValuationsDialog';
import { VacancyPanel } from './VacancyPanel';
import { ACTUAL_ANNUALIZED_HELP, ANNUAL_ESTIMATE_HELP } from './metrics-copy';

interface PerformanceTabProps {
  investmentId: string;
  investment: InvestmentDTO;
  canEdit: boolean;
}

type Scope = 'asset' | 'mine';

const CASHFLOW_KEYS = [
  'operatingIncome',
  'operatingExpenses',
  'interestPaid',
  'principalRepaid',
  'capexPaid',
] as const;

const CASH_BASIS_HELP =
  'Los importes son de caja: cuentan los cobros y pagos con fecha dentro del periodo, no lo devengado.';

const chipStyles = { height: 24, '& .MuiChip-icon': { fontSize: '0.95rem' } } as const;

const buildYearOptions = (investment: InvestmentDTO): number[] => {
  const currentYear = new Date().getUTCFullYear();
  const firstYear = new Date(investment.purchaseDate ?? investment.createdAt).getUTCFullYear();
  const from = Number.isNaN(firstYear) ? currentYear : Math.min(firstYear, currentYear);

  return Array.from({ length: currentYear - from + 1 }, (_, index) => currentYear - index);
};

const loadingSkeleton = (
  <Stack spacing={4}>
    <Skeleton variant="rounded" height={96} />
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Skeleton variant="rounded" height={280} />
      </Grid>
      <Grid item xs={12} md={7}>
        <Skeleton variant="rounded" height={280} />
      </Grid>
    </Grid>
    <Skeleton variant="rounded" height={160} />
    <Skeleton variant="rounded" height={160} />
  </Stack>
);

export const PerformanceTab = ({ investmentId, investment, canEdit }: PerformanceTabProps) => {
  const currentYear = new Date().getUTCFullYear();
  const yearOptions = useMemo(() => buildYearOptions(investment), [investment]);
  const [selectedYear, setSelectedYear] = useState(() => yearOptions[0] ?? currentYear);
  const [scope, setScope] = useState<Scope>('asset');
  const [isValuationsOpen, setIsValuationsOpen] = useState(false);
  const [isClassifyOpen, setIsClassifyOpen] = useState(false);

  const {
    data: metrics,
    isLoading,
    isError,
  } = useGetInvestmentFinancialMetrics(investmentId, selectedYear);

  const ownership = metrics?.ownershipPercentage ?? 1;
  const canSplitByOwnership = ownership > 0 && ownership < 1;
  const isProrated = scope === 'mine' && canSplitByOwnership;

  const scaleAmount = useCallback(
    (cents: number) => (isProrated ? Math.round(cents * ownership) : cents),
    [isProrated, ownership]
  );

  const openValuations = useCallback(() => setIsValuationsOpen(true), []);
  const closeValuations = useCallback(() => setIsValuationsOpen(false), []);
  const openClassify = useCallback(() => setIsClassifyOpen(true), []);
  const closeClassify = useCallback(() => setIsClassifyOpen(false), []);
  const handleScopeChange = useCallback((_: unknown, next: Scope | null) => {
    if (next) setScope(next);
  }, []);
  const handleYearChange = useCallback(
    (event: SelectChangeEvent<number>) => setSelectedYear(Number(event.target.value)),
    []
  );

  if (isError) {
    return (
      <Alert severity="error">
        No se pudo calcular la rentabilidad de esta inversión. Vuelve a intentarlo más tarde.
      </Alert>
    );
  }

  if (isLoading || !metrics) return loadingSkeleton;

  const hasMovements = CASHFLOW_KEYS.some((key) => (metrics.metrics[key].value ?? 0) !== 0);
  const showVacancy = metrics.vacancy.coverage !== 'NONE' && metrics.vacancy.availableDays > 0;

  return (
    <Stack spacing={4}>
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'grey.50', borderColor: 'grey.100' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Rentabilidad {metrics.period.year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Del alquiler cobrado al retorno sobre el capital que tienes invertido.
            </Typography>
          </Box>

          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            {canSplitByOwnership ? (
              <ToggleButtonGroup
                value={scope}
                exclusive
                size="small"
                onChange={handleScopeChange}
                aria-label="Ámbito de los importes"
                sx={{ bgcolor: 'background.paper' }}
              >
                <ToggleButton value="asset" sx={{ px: 2, textTransform: 'none' }}>
                  Activo completo
                </ToggleButton>
                <ToggleButton value="mine" sx={{ px: 2, textTransform: 'none' }}>
                  Mi parte
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null}

            <FormControl size="small" sx={{ minWidth: 168, bgcolor: 'background.paper' }}>
              <InputLabel id="performance-year-label">Ejercicio</InputLabel>
              <Select
                labelId="performance-year-label"
                label="Ejercicio"
                value={selectedYear}
                onChange={handleYearChange}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year === currentYear ? `${year} (en curso)` : year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
          <Tooltip title={CASH_BASIS_HELP} arrow>
            <Chip
              size="small"
              variant="outlined"
              icon={<EventAvailableOutlinedIcon />}
              label={
                metrics.period.isYearToDate
                  ? `Real hasta el ${formatDate(metrics.period.endDate)}`
                  : `Ejercicio completo ${metrics.period.year}`
              }
              sx={{ ...chipStyles, cursor: 'help', bgcolor: 'background.paper' }}
            />
          </Tooltip>

          {metrics.actualAnnualized ? (
            <Tooltip title={ACTUAL_ANNUALIZED_HELP} arrow>
              <Chip
                size="small"
                variant="outlined"
                icon={<InsightsOutlinedIcon />}
                label="Ritmo anualizado YTD"
                sx={{ ...chipStyles, cursor: 'help', bgcolor: 'background.paper' }}
              />
            </Tooltip>
          ) : null}

          {metrics.annualEstimate ? (
            <Tooltip title={ANNUAL_ESTIMATE_HELP} arrow>
              <Chip
                size="small"
                variant="outlined"
                icon={<InsightsOutlinedIcon />}
                label="Previsión anual según ficha"
                sx={{ ...chipStyles, cursor: 'help', bgcolor: 'background.paper' }}
              />
            </Tooltip>
          ) : null}

          {isProrated ? (
            <Tooltip
              title="Los importes se prorratean a tu participación. Los porcentajes de rentabilidad no cambian."
              arrow
            >
              <Chip
                size="small"
                variant="outlined"
                color="secondary"
                icon={<PieChartOutlineIcon />}
                label={`Prorrateado al ${formatPercentage(ownership)}`}
                sx={{ ...chipStyles, cursor: 'help', bgcolor: 'background.paper' }}
              />
            </Tooltip>
          ) : null}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <PositionCard
            metrics={metrics}
            scaleAmount={scaleAmount}
            canEdit={canEdit}
            onManageValuations={openValuations}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <TotalReturnCard
            metrics={metrics}
            scaleAmount={scaleAmount}
            canEdit={canEdit}
            onManageValuations={openValuations}
          />
        </Grid>
      </Grid>

      <Box component="section">
        <SectionHeading
          step={1}
          title="Rentabilidad del activo"
          description="Sin contar la hipoteca: qué renta el inmueble por sí mismo."
        />
        <AssetReturnCards metrics={metrics} scaleAmount={scaleAmount} />
      </Box>

      <Box component="section">
        <SectionHeading
          step={2}
          title="Rentabilidad de tu capital"
          description="Con la hipoteca descontada: qué rinde el dinero que tienes inmovilizado."
        />
        <EquityReturnCards metrics={metrics} scaleAmount={scaleAmount} />
      </Box>

      <Box component="section">
        <SectionHeading
          step={3}
          title="Recorrido del dinero"
          description="Qué entra, qué sale y qué queda al final del periodo."
        />
        {hasMovements ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <CashflowWaterfall metrics={metrics} scaleAmount={scaleAmount} />
            </Grid>
            <Grid item xs={12} md={6}>
              <OperatingExpensesBreakdown metrics={metrics} scaleAmount={scaleAmount} />
            </Grid>
          </Grid>
        ) : (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                component={RouterLink}
                to={`/inversiones/${investmentId}?tab=transactions`}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Ir a Transacciones
              </Button>
            }
          >
            No hay movimientos registrados en {metrics.period.year}. Registra los cobros del
            alquiler y los gastos para ver la rentabilidad real de este ejercicio.
          </Alert>
        )}
      </Box>

      <Box component="section">
        <SectionHeading
          step={4}
          title="Deuda y ocupación"
          description="Las dos palancas que más mueven el retorno: cómo va el préstamo y cuánto tiempo está alquilado."
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={showVacancy ? 7 : 12}>
            <FinancingPanel
              investmentId={investmentId}
              metrics={metrics}
              scaleAmount={scaleAmount}
            />
          </Grid>
          {showVacancy ? (
            <Grid item xs={12} md={5}>
              <VacancyPanel metrics={metrics} scaleAmount={scaleAmount} />
            </Grid>
          ) : null}
        </Grid>
      </Box>

      <DataQualityPanel metrics={metrics} canEdit={canEdit} onReviewClassification={openClassify} />

      {canEdit ? (
        <>
          <ValuationsDialog
            open={isValuationsOpen}
            investmentId={investmentId}
            onClose={closeValuations}
          />
          <ClassifyLinesDialog
            open={isClassifyOpen}
            investmentId={investmentId}
            onClose={closeClassify}
          />
        </>
      ) : null}
    </Stack>
  );
};
